import { SlideshowSelectEvent } from '@theme/events';
import { mediaQueryLarge } from '@theme/utilities';
import '@theme/dialog';

/** Optional PDP controls; slide selection stays owned by the native slideshow. */
class ProductGalleryNavigation extends HTMLElement {
  #controller;
  #request = 0;
  #announcement = 0;
  #announcementTimer;
  #clearAnnouncementTimer;
  #originalAlts = new WeakMap();

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('click', this.#click, { signal });
    this.closest('media-gallery')?.addEventListener(SlideshowSelectEvent.eventName, this.#sync, { signal });
    mediaQueryLarge.addEventListener('change', this.#sync, { signal });
    customElements.whenDefined('slideshow-component').then(() => {
      if (this.isConnected && !signal.aborted) this.#sync();
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#request++;
    this.#announcement++;
    clearTimeout(this.#announcementTimer);
    clearTimeout(this.#clearAnnouncementTimer);
  }

  get slideshow() {
    return this.closest('media-gallery')?.querySelector('slideshow-component');
  }

  #sync = (event) => {
    const slideshow = this.slideshow;
    if (!slideshow || (event?.type === SlideshowSelectEvent.eventName && event.target !== slideshow)) return;
    const slides = slideshow.slides || [];
    const current = slideshow.current || 0;
    const desktopMinimal = mediaQueryLarge.matches && this.dataset.desktopMinimal === 'true' && slides.length > 1;
    const mobilePicker = !mediaQueryLarge.matches && this.dataset.mobilePicker === 'true';
    const position = (index) => this.dataset.positionTemplate.replace('__index__', String(index + 1));

    const count = this.querySelector('[data-gallery-current]');
    if (count) count.textContent = String(current + 1);
    const label = this.querySelector('[data-gallery-picker-label]');
    const selectedImage = slides[current]?.querySelector('img.product-media__image');
    if (label) {
      const alt = this.#originalAlts.get(selectedImage) ?? selectedImage?.getAttribute('alt') ?? '';
      label.textContent = `${this.dataset.pickerLabel}. ${position(current)}${alt ? `. ${alt}` : ''}`;
    }
    this.querySelectorAll('[data-gallery-choice]').forEach((choice) => {
      if (Number(choice.dataset.galleryChoice) === current) choice.setAttribute('aria-current', 'true');
      else choice.removeAttribute('aria-current');
    });
    slides.forEach((slide, index) => {
      // Keep video/model controls exposed; only the redundant still image is hidden on desktop.
      if (!slide.classList.contains('product-media-container--image')) return;
      const image = slide.querySelector('img.product-media__image');
      if (!image) return;
      if (!this.#originalAlts.has(image)) this.#originalAlts.set(image, image.getAttribute('alt') || '');
      const alt = this.#originalAlts.get(image);
      image.setAttribute('alt', mobilePicker ? `${position(index)}${alt ? `. ${alt}` : ''}` : alt);
      if (desktopMinimal) image.setAttribute('aria-hidden', 'true');
      else image.removeAttribute('aria-hidden');
    });
    const mobileSwipeAnnouncements = !mediaQueryLarge.matches &&
      (this.dataset.mobilePicker === 'true' || this.dataset.mobileThumbnails === 'true');
    this.#announceSwipe(event, slideshow, mobileSwipeAnnouncements);
    const zoom = this.querySelector('[data-gallery-zoom]');
    if (zoom) zoom.hidden = !(desktopMinimal || mobilePicker) || !slides[current]?.classList.contains('product-media-container--image');
  };

  #announceSwipe(event, slideshow, enabled) {
    const request = ++this.#announcement;
    clearTimeout(this.#announcementTimer);
    clearTimeout(this.#clearAnnouncementTimer);
    const status = this.querySelector('[data-gallery-swipe-status]');
    if (!status) return;
    status.textContent = '';
    if (!enabled || event?.detail?.trigger !== 'scroll' || !event.detail.userInitiated) return;

    // Coalesce consecutive swipes and wait for the final image without moving focus.
    this.#announcementTimer = setTimeout(async () => {
      await slideshow.waitForIdle();
      if (!this.isConnected || request !== this.#announcement || mediaQueryLarge.matches) return;
      if (this.closest('[inert]') || this.querySelector('dialog[open]')) return;
      const index = slideshow.current;
      const image = slideshow.slides?.[index]?.querySelector('img.product-media__image');
      const alt = this.#originalAlts.get(image) ?? image?.getAttribute('alt') ?? '';
      const position = this.dataset.positionTemplate.replace('__index__', String(index + 1));
      status.textContent = `${position}${alt ? `. ${alt}` : ''}`;
      this.#clearAnnouncementTimer = setTimeout(() => {
        if (request === this.#announcement) status.textContent = '';
      }, 10000);
    }, 250);
  }

  #click = async (event) => {
    if (!(event.target instanceof Element)) return;
    const slideshow = this.slideshow;
    if (!slideshow) return;
    if (event.target.closest('[data-gallery-zoom]')) {
      this.closest('media-gallery')?.querySelector('zoom-dialog')?.open(slideshow.current, event);
      return;
    }
    const choice = event.target.closest('[data-gallery-choice]');
    if (!choice) return;
    const request = ++this.#request;
    const index = Number(choice.dataset.galleryChoice);
    const mediaId = slideshow.slides?.[index]?.getAttribute('slide-id');
    if (!mediaId) return;
    await slideshow.waitForIdle();
    if (!this.isConnected || request !== this.#request || !this.querySelector('dialog[open]') || mediaQueryLarge.matches) return;
    await slideshow.select({ id: mediaId }, undefined, { animate: false });
    this.#sync();
    await this.querySelector('dialog-component')?.closeDialog();
    if (!this.isConnected || request !== this.#request || mediaQueryLarge.matches) return;
    const trigger = this.querySelector('[data-gallery-picker-trigger]');
    trigger?.focus({ preventScroll: true });
    trigger?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
  };
}

if (!customElements.get('product-gallery-navigation')) {
  customElements.define('product-gallery-navigation', ProductGalleryNavigation);
}
